import type { ClinicalEfficiencyKpiData } from '@services/api/dashboard/clinical-efficiency.api'
import type { DashboardMetricItemData } from '../types/dashboardMetrics.types'
import {
  getOverviewUtilizationRow3Metrics,
  getOverviewFrontDeskMetricsRow,
} from '@components/pages/Dashboard/Overview/overviewMetrics.mockData'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  isDashboardDevMocksEnabled,
  withDashboardMetricShellValues,
} from '@utils/dashboardDevMocks.util'

export function mapClinicalEfficiencyKpisToMetrics(
  kpis: ClinicalEfficiencyKpiData,
): DashboardMetricItemData[] {
  return [
    {
      id: 'avg-time',
      leftLabel: 'Avg. time per patient',
      rightLabel: kpis.avgTimeGoal,
      value: kpis.avgTimePerPatient,
    },
    {
      id: 'avg-provider-util',
      leftLabel: 'Avg. provider utilization',
      rightLabel: kpis.utilizationGoal,
      value: kpis.avgProviderUtilization,
    },
    {
      id: 'tx-opportunities',
      leftLabel: 'Treatment opportunities',
      ...(kpis.treatmentOpportunitiesGoal
        ? { rightLabel: kpis.treatmentOpportunitiesGoal }
        : {}),
      value: kpis.treatmentOpportunities,
    },
  ]
}

export function buildClinicalEfficiencyMetrics(
  range: RangeKey,
  kpis: ClinicalEfficiencyKpiData | null | undefined,
): DashboardMetricItemData[] {
  if (!kpis) {
    return withDashboardMetricShellValues(
      getOverviewUtilizationRow3Metrics(range),
      isDashboardDevMocksEnabled(),
    )
  }
  return mapClinicalEfficiencyKpisToMetrics(kpis)
}

/** Overlay live clinical-efficiency values onto mock metrics by id (with optional id aliases). */
export function mergeClinicalEfficiencyIntoMetrics(
  mockMetrics: DashboardMetricItemData[],
  liveMetrics: DashboardMetricItemData[],
  idAliases: Record<string, string> = {},
): DashboardMetricItemData[] {
  const liveById = new Map(
    liveMetrics
      .filter((metric) => metric.id)
      .map((metric) => [metric.id as string, metric]),
  )
  return mockMetrics.map((metric) => {
    if (!metric.id) {
      return metric
    }
    const liveId = idAliases[metric.id] ?? metric.id
    const live = liveById.get(liveId)
    if (!live) {
      return metric
    }
    return {
      ...metric,
      value: live.value,
      rightLabel: live.rightLabel ?? metric.rightLabel,
    }
  })
}

const FRONT_DESK_CLINICAL_EFFICIENCY_ID_ALIASES: Record<string, string> = {
  'fd-avg-time': 'avg-time',
  'fd-avg-provider-util': 'avg-provider-util',
}

const OWNER_CLINICAL_EFFICIENCY_ID_ALIASES: Record<string, string> = {
  'owner-avg-provider-util': 'avg-provider-util',
  'owner-tx-opportunities': 'tx-opportunities',
}

export function buildFrontDeskMetricsRow(
  range: RangeKey,
  liveMetrics: DashboardMetricItemData[],
): DashboardMetricItemData[] {
  return mergeClinicalEfficiencyIntoMetrics(
    withDashboardMetricShellValues(
      getOverviewFrontDeskMetricsRow(range),
      isDashboardDevMocksEnabled(),
    ),
    liveMetrics,
    FRONT_DESK_CLINICAL_EFFICIENCY_ID_ALIASES,
  )
}

export function mergeOwnerClinicalEfficiencyKpis(
  metrics: DashboardMetricItemData[],
  liveMetrics: DashboardMetricItemData[],
): DashboardMetricItemData[] {
  return mergeClinicalEfficiencyIntoMetrics(
    metrics,
    liveMetrics,
    OWNER_CLINICAL_EFFICIENCY_ID_ALIASES,
  )
}
